import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import { useAnalytics } from '@/hooks/useAnalytics';

interface SubscriptionStatus {
  subscribed: boolean;
  trial_active: boolean;
  subscription_end: string | null;
  trial_end: string | null;
  subscription_tier: string | null;
}

export const useSubscription = () => {
  const { user, session } = useAuth();
  const { profile } = useProfile();
  const { trackTrialStarted, trackSubscriptionCreated } = useAnalytics();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    subscribed: false,
    trial_active: false,
    subscription_end: null,
    trial_end: null,
    subscription_tier: null,
  });
  const [loading, setLoading] = useState(true);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  const [trialStatusData, setTrialStatusData] = useState<any>(null);

  // Lightweight poll: only calls the DB RPC
  const refreshTrialStatus = useCallback(async () => {
    if (!user) return;
    try {
      const { data: trialStatus, error: trialError } = await supabase
        .rpc('calculate_trial_status', { p_user_id: user.id });
      if (trialError) {
        console.error('Error calculating trial status:', trialError);
      } else {
        setTrialStatusData(trialStatus);
      }
    } catch (error) {
      console.error('Error refreshing trial status:', error);
    }
  }, [user]);

  // Load subscription from DB cache (no Stripe API call)
  const loadSubscriptionFromDB = useCallback(async () => {
    if (!user || !session) {
      setLoading(false);
      return;
    }

    try {
      // Get trial status from centralized database function
      const { data: trialStatus, error: trialError } = await supabase
        .rpc('calculate_trial_status', { p_user_id: user.id });
      
      if (trialError) {
        console.error('Error calculating trial status:', trialError);
      } else {
        setTrialStatusData(trialStatus);
      }

      // Read cached subscription data from DB (via get-subscription)
      const { data, error } = await supabase.functions.invoke('get-subscription');
      
      if (error) {
        console.error('Error loading subscription from DB:', error);
        return;
      }

      if (data) {
        setSubscriptionStatus({
          subscribed: data.subscribed || false,
          trial_active: data.trial_active || false,
          subscription_end: data.current_period_end || null,
          trial_end: data.trial_end || null,
          subscription_tier: data.subscription_tier || null,
        });
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [user, session]);

  // Full sync with Stripe API — only for manual refresh or post-checkout
  const syncWithStripe = useCallback(async () => {
    if (!user || !session) return;

    setCheckingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error('Error syncing with Stripe:', error);
        return;
      }

      if (data) {
        setSubscriptionStatus({
          subscribed: data.subscribed || false,
          trial_active: data.trial_active || false,
          subscription_end: data.subscription_end || null,
          trial_end: data.trial_end || null,
          subscription_tier: data.subscription_tier || null,
        });
      }

      // Also refresh trial status
      await refreshTrialStatus();
    } catch (error) {
      console.error('Error syncing with Stripe:', error);
    } finally {
      setCheckingSubscription(false);
    }
  }, [user, session, refreshTrialStatus]);



  const createCheckout = async (subscriptionTier?: 'monthly' | 'yearly'): Promise<string | undefined> => {
    if (!user) {
      toast.error('Please sign in to upgrade');
      return undefined;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { subscriptionTier: subscriptionTier || 'yearly' }
      });
      
      if (error) {
        console.error('Error creating checkout:', error);
        toast.error('Failed to create checkout session');
        return undefined;
      }

      if (data?.clientSecret) {
        trackSubscriptionCreated(subscriptionTier || 'yearly');
        return data.clientSecret;
      }

      return undefined;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout session');
      return undefined;
    }
  };

  const openCustomerPortal = async () => {
    if (!user) {
      toast.error('Please sign in to manage subscription');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) {
        console.error('Error opening customer portal:', error);
        toast.error('Failed to open subscription management');
        return;
      }

      if (data?.url) {
        toast.success('Redirecting to billing portal...');
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open subscription management');
    }
  };

  const getTrialDaysRemaining = () => {
    // Prefer trial status from database function (paid trials)
    if (trialStatusData?.trial_days_remaining !== undefined) {
      return Math.ceil(trialStatusData.trial_days_remaining);
    }
    // Fallback to subscription status
    if (!subscriptionStatus.trial_end) return 0;
    const trialEnd = new Date(subscriptionStatus.trial_end);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getFreeTrialDaysRemaining = () => {
    // Use database function calculation
    if (trialStatusData?.free_trial_active && trialStatusData?.trial_days_remaining !== undefined) {
      return Math.ceil(trialStatusData.trial_days_remaining);
    }
    return 0;
  };

  const isFreeTrialActive = () => {
    return trialStatusData?.free_trial_active === true;
  };

  const hasUnlimitedAccess = trialStatusData?.unlimited_access === true || 
                             profile?.invitation_code_used === "okie";
  
  // Calculate trial stage and access status from database function
  const getDaysIntoTrial = () => {
    if (trialStatusData?.trial_days_into !== undefined) {
      return Math.floor(trialStatusData.trial_days_into);
    }
    return 0;
  };

  const daysIntoTrial = getDaysIntoTrial();
  const freeTrialDaysRemaining = getFreeTrialDaysRemaining();
  const isTrialExpired = trialStatusData?.trial_expired === true;

  // Trial stage from database function
  const getTrialStage = (): 'early' | 'awareness' | 'urgency' | 'expired' => {
    if (trialStatusData?.trial_stage) {
      return trialStatusData.trial_stage;
    }
    if (isTrialExpired) return 'expired';
    return 'early';
  };

  const trialStage = getTrialStage();

  // User can access app based on database function result
  const canAccessApp = trialStatusData?.has_access === true;

  const isPremium = trialStatusData?.is_premium === true;

  useEffect(() => {
    if (profile !== null) {
      loadSubscriptionFromDB();
    }
  }, [profile, loadSubscriptionFromDB]);

  // Lightweight periodic refresh (DB RPC only, no Stripe API call)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      refreshTrialStatus();
    }, 300000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [user, refreshTrialStatus]);

  return {
    ...subscriptionStatus,
    isPremium,
    hasUnlimitedAccess,
    loading,
    checkingSubscription,
    trialDaysRemaining: getTrialDaysRemaining(),
    freeTrialDaysRemaining,
    isFreeTrialActive: isFreeTrialActive(),
    daysIntoTrial,
    trialStage,
    isTrialExpired,
    canAccessApp,
    checkSubscription: syncWithStripe,
    createCheckout,
    openCustomerPortal,
  };
};